// src/shared/utils/perf.ts

export interface PerfSample {
  name: string;
  start: number;
  end: number;
  duration: number;
  meta?: Record<string, unknown>;
}

type PerfStore = Record<string, PerfSample[]>;

const PERF_STORE_KEY = '__VIDEO_GRID_PERF__';
const PERF_DEBUG_STORAGE_KEY = 'videoGrid_perf_debug';
const PERF_DEBUG_ENV_KEY = 'VITE_VIDEO_GRID_PERF_DEBUG';
const MAX_SAMPLES_PER_NAME = 200;
const markStarts = new Map<string, number>();
const TRUE_VALUES = ['1', 'true', 'yes', 'on'];
const FALSE_VALUES = ['0', 'false', 'no', 'off'];

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function parseBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized.length === 0) {
    return null;
  }

  if (TRUE_VALUES.includes(normalized)) {
    return true;
  }

  if (FALSE_VALUES.includes(normalized)) {
    return false;
  }

  return null;
}

export function isPerfDebugEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const localStorageValue = window.localStorage.getItem(PERF_DEBUG_STORAGE_KEY);
    const parsedLocalStorage = parseBoolean(localStorageValue);
    if (parsedLocalStorage != null) {
      return parsedLocalStorage;
    }
  } catch {
    // Ignore localStorage access errors and fall through to env/default.
  }

  const parsedEnv = parseBoolean(import.meta.env[PERF_DEBUG_ENV_KEY]);
  return parsedEnv ?? false;
}

function getPerfStore(): PerfStore | null {
  if (typeof window === 'undefined') return null;
  const win = window as Window & { [PERF_STORE_KEY]?: PerfStore };
  if (!win[PERF_STORE_KEY]) {
    win[PERF_STORE_KEY] = {};
  }
  return win[PERF_STORE_KEY]!;
}

function pushSample(
  name: string,
  start: number,
  end: number,
  meta?: Record<string, unknown>
): PerfSample {
  const sample: PerfSample = {
    name,
    start,
    end,
    duration: Math.max(0, end - start),
    meta,
  };

  const store = getPerfStore();
  if (store) {
    const bucket = store[name] ?? [];
    bucket.push(sample);
    if (bucket.length > MAX_SAMPLES_PER_NAME) {
      bucket.shift();
    }
    store[name] = bucket;
  }

  return sample;
}

export function markStart(name: string): void {
  markStarts.set(name, now());
}

export function markEnd(name: string, meta?: Record<string, unknown>): number {
  const start = markStarts.get(name);
  if (start == null) return 0;

  const end = now();
  markStarts.delete(name);
  const sample = pushSample(name, start, end, meta);
  return sample.duration;
}

export function measure<T>(
  name: string,
  fn: () => T,
  meta?: Record<string, unknown>
): T {
  const start = now();
  try {
    return fn();
  } finally {
    pushSample(name, start, now(), meta);
  }
}

export function recordDuration(
  name: string,
  durationMs: number,
  meta?: Record<string, unknown>
): number {
  const end = now();
  const start = end - Math.max(0, durationMs);
  const sample = pushSample(name, start, end, meta);
  return sample.duration;
}

export function recordSample(
  name: string,
  value: number,
  meta?: Record<string, unknown>
): number {
  const time = now();
  pushSample(name, time, time, {
    value,
    ...meta,
  });
  return value;
}
